import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calculator, Hammer, Paintbrush, Layers, Construction,
  FileText, Clock, Link2, Trash2, Search, ArrowUpDown,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CALCULATORS = [
  {
    slug: 'concrete',
    name: 'Concrete / Flatwork',
    desc: 'Slab, walkway, driveway, or footing. Calculates area, excavation, concrete volume, labour hours and materials with markup.',
    icon: Construction,
    live: true,
  },
  { slug: 'framing', name: 'Framing', desc: 'Stud count, plates, headers and nails based on wall length + height.', icon: Hammer, live: false },
  { slug: 'drywall', name: 'Drywall', desc: 'Sheets, screws, tape and mud based on wall/ceiling square footage.', icon: Layers, live: true },
  { slug: 'painting', name: 'Painting', desc: 'Primer + paint gallons, tape & drop cloths based on wall area and coats.', icon: Paintbrush, live: true },
];

const TYPE_META = {
  concrete: { label: 'Concrete', icon: Construction, color: 'text-[#2D6B68]' },
  drywall:  { label: 'Drywall',  icon: Layers,       color: 'text-[#2D6B68]' },
  painting: { label: 'Painting', icon: Paintbrush,   color: 'text-[#2D6B68]' },
};

export default function PriceBook() {
  const navigate = useNavigate();
  const [saves, setSaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('updated'); // updated | created | name | type
  const [filterType, setFilterType] = useState('all');

  const load = () => {
    setLoading(true);
    fetch(`${API}/price-book/saves`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(setSaves)
      .catch(() => setSaves([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const deleteSave = async (save_id) => {
    if (!window.confirm('Delete this saved calculation?')) return;
    const res = await fetch(`${API}/price-book/saves/${save_id}`, {
      method: 'DELETE', credentials: 'include',
    });
    if (res.ok) {
      setSaves(prev => prev.filter(s => s.save_id !== save_id));
      toast.success('Deleted');
    } else {
      toast.error('Delete failed');
    }
  };

  const filtered = useMemo(() => {
    let list = saves;
    if (filterType !== 'all') list = list.filter(s => s.calculator_type === filterType);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter(s =>
      (s.name || '').toLowerCase().includes(q)
      || (s.estimate_summary?.estimate_number || '').toLowerCase().includes(q)
      || (s.calculator_type || '').toLowerCase().includes(q)
    );
    const ord = [...list];
    ord.sort((a, b) => {
      if (sort === 'name') return (a.name || '').localeCompare(b.name || '');
      if (sort === 'type') return (a.calculator_type || '').localeCompare(b.calculator_type || '');
      if (sort === 'created') return (b.created_at || '').localeCompare(a.created_at || '');
      return (b.updated_at || '').localeCompare(a.updated_at || '');
    });
    return ord;
  }, [saves, search, sort, filterType]);

  return (
    <div className="animate-fade-in" data-testid="price-book-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D1C]" style={{ fontFamily: "'Chivo', sans-serif" }}>
          Price Book
        </h1>
        <p className="text-sm text-[#6B7A73] mt-1">
          Calculators for quick estimate pricing. Saved calculations are listed below.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {CALCULATORS.map(c => {
          const Icon = c.icon;
          const disabled = !c.live;
          return (
            <button
              key={c.slug}
              onClick={() => !disabled && navigate(`/price-book/${c.slug}`)}
              disabled={disabled}
              data-testid={`calc-card-${c.slug}`}
              className={`group text-left bg-white border border-[#E6EBE8] rounded-md p-5 transition-all ${
                disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-[#2D6B68] hover:shadow-sm'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-10 h-10 rounded-md flex items-center justify-center ${
                  disabled ? 'bg-[#F0F3F1]' : 'bg-[#E5F0EE] group-hover:bg-[#2D6B68]'
                }`}>
                  <Icon className={`w-5 h-5 ${disabled ? 'text-[#6B7A73]' : 'text-[#2D6B68] group-hover:text-white'}`} />
                </div>
                <h2 className="text-base font-semibold text-[#1A1D1C]" style={{ fontFamily: "'Chivo', sans-serif" }}>
                  {c.name}
                </h2>
                {!c.live && (
                  <span className="ml-auto text-[10px] uppercase tracking-wider text-[#6B7A73] bg-[#F0F3F1] px-1.5 py-0.5 rounded">
                    Soon
                  </span>
                )}
              </div>
              <p className="text-sm text-[#6B7A73] leading-relaxed">{c.desc}</p>
            </button>
          );
        })}
      </div>

      {/* ── Saved calculations list ──────────────────────────────── */}
      <div className="mt-10">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
          <div>
            <h2 className="text-lg font-semibold text-[#1A1D1C]" style={{ fontFamily: "'Chivo', sans-serif" }}>
              Saved calculations
            </h2>
            <p className="text-xs text-[#6B7A73]">Pick up where you left off — click a row to re-open.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6B7A73]" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search…"
                className="bg-white border border-[#E6EBE8] rounded-md pl-8 pr-3 py-1.5 text-sm w-48 focus:outline-none focus:border-[#2D6B68]"
                data-testid="pb-saves-search"
              />
            </div>
            <select
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
              className="bg-white border border-[#E6EBE8] rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-[#2D6B68]"
              data-testid="pb-saves-filter"
            >
              <option value="all">All types</option>
              <option value="concrete">Concrete</option>
              <option value="drywall">Drywall</option>
              <option value="painting">Painting</option>
            </select>
            <select
              value={sort}
              onChange={e => setSort(e.target.value)}
              className="bg-white border border-[#E6EBE8] rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-[#2D6B68]"
              data-testid="pb-saves-sort"
            >
              <option value="updated">Last updated</option>
              <option value="created">Created</option>
              <option value="name">Name</option>
              <option value="type">Type</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="bg-white border border-[#E6EBE8] rounded-md p-8 text-center text-sm text-[#6B7A73]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-[#E6EBE8] rounded-md p-8 text-center text-sm text-[#6B7A73]">
            No saved calculations yet. Open a calculator, fill it in, and hit <strong>Save</strong>.
          </div>
        ) : (
          <div className="bg-white border border-[#E6EBE8] rounded-md overflow-hidden" data-testid="pb-saves-list">
            <table className="w-full text-sm">
              <thead className="bg-[#F9FAFA] border-b border-[#E6EBE8]">
                <tr className="text-left text-xs uppercase tracking-wider text-[#6B7A73]">
                  <th className="py-2.5 px-3 font-semibold">Name</th>
                  <th className="py-2.5 px-3 font-semibold">Type</th>
                  <th className="py-2.5 px-3 font-semibold">Estimate</th>
                  <th className="py-2.5 px-3 font-semibold">Total</th>
                  <th className="py-2.5 px-3 font-semibold">Created</th>
                  <th className="py-2.5 px-3 font-semibold">Updated</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => {
                  const meta = TYPE_META[s.calculator_type] || { label: s.calculator_type, icon: Calculator, color: 'text-[#6B7A73]' };
                  const Icon = meta.icon;
                  const total = s.summary_totals?.total_quote;
                  return (
                    <tr
                      key={s.save_id}
                      className="border-b border-[#F0F3F1] last:border-b-0 hover:bg-[#F9FAFA] cursor-pointer"
                      onClick={() => navigate(`/price-book/${s.calculator_type}?save=${s.save_id}`)}
                      data-testid={`pb-save-row-${s.save_id}`}
                    >
                      <td className="py-3 px-3 text-[#1A1D1C] font-medium">{s.name}</td>
                      <td className="py-3 px-3 text-[#3A423E]">
                        <span className="inline-flex items-center gap-1.5">
                          <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
                          {meta.label}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        {s.estimate_summary?.estimate_number ? (
                          <span
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border text-[#C7892A] bg-amber-50 border-amber-200"
                            title={s.estimate_summary.title}
                          >
                            <Link2 className="w-2.5 h-2.5" /> {s.estimate_summary.estimate_number}
                          </span>
                        ) : (
                          <span className="text-[11px] text-[#9BA5A0]">—</span>
                        )}
                      </td>
                      <td className="py-3 px-3 text-[#3A423E] font-mono">
                        {typeof total === 'number' ? `$${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                      </td>
                      <td className="py-3 px-3 text-[#6B7A73] text-xs">
                        {s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-3 px-3 text-[#6B7A73] text-xs">
                        {s.updated_at ? new Date(s.updated_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-3 px-3 text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteSave(s.save_id); }}
                          className="p-1.5 text-[#6B7A73] hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                          data-testid={`pb-delete-${s.save_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
